import { defineStore } from 'pinia'
import { useToast } from 'vuestic-ui'
import { api } from '../services/api'
import { useAppStore } from './appStore'

export type TenantAddress = {
  id?: number
  zipcode: string
  ibge_code: string
  state: string
  city: string
  district: string
  street: string
  number: string
  complement: string
}

export type TenantCustomer = {
  id: number
  name: string
  email?: string
  phone?: string
}

export type Tenant = {
  id?: number
  name: string
  company_name: string
  federal_id: string
  municipal_registration: string | null
  email: string
  phone: string | null
  active: boolean
  fiscal_profile_complete?: boolean
  missing_fiscal_fields?: string[]
  address?: TenantAddress | null
  customers?: TenantCustomer[]
}

export type PersistTenantPayload = {
  name: string
  company_name: string
  federal_id: string
  municipal_registration?: string | null
  email: string
  phone?: string | null
  active?: boolean
  address?: Omit<TenantAddress, 'id'>
}

export const missingFiscalFieldLabels: Record<string, string> = {
  federal_id: 'CNPJ',
  company_name: 'Razão Social',
  email: 'E-mail',
  phone: 'Telefone',
  'address.zipcode': 'CEP',
  'address.ibge_code': 'Código IBGE',
  'address.state': 'UF',
  'address.city': 'Cidade',
  'address.district': 'Bairro',
  'address.street': 'Rua',
  'address.number': 'Número',
}

const ADDRESS_VALUE_KEYS: Array<keyof TenantAddress> = [
  'zipcode',
  'ibge_code',
  'state',
  'city',
  'district',
  'street',
  'number',
  'complement',
]

export const onlyDigits = (value?: string | null) => (value ?? '').replace(/\D/g, '')

export const emptyAddress = (): TenantAddress => ({
  zipcode: '',
  ibge_code: '',
  state: '',
  city: '',
  district: '',
  street: '',
  number: '',
  complement: '',
})

export const hasAnyAddressValue = (address?: TenantAddress | null) => {
  if (!address) {
    return false
  }

  return ADDRESS_VALUE_KEYS.some((key) => String(address[key] ?? '').trim() !== '')
}

const emptyTenant = (): Tenant => ({
  name: '',
  company_name: '',
  federal_id: '',
  municipal_registration: '',
  email: '',
  phone: '',
  active: true,
  address: emptyAddress(),
  customers: [],
})

const getErrorMessage = (error: any) => {
  const data = error?.response?.data

  if (data?.errors && typeof data.errors === 'object') {
    const firstFieldErrors = Object.values(data.errors)[0]

    if (Array.isArray(firstFieldErrors) && typeof firstFieldErrors[0] === 'string') {
      return firstFieldErrors[0]
    }
  }

  if (typeof data?.message === 'string') {
    return data.message
  }

  return error?.message || 'Erro inesperado'
}

const getResponseMessage = (payload: unknown, fallback: string) => {
  return typeof payload === 'object' && payload !== null && 'message' in payload && typeof payload.message === 'string'
    ? payload.message
    : fallback
}

export const useCompanyStore = defineStore('company', {
  state: () => ({
    filters: {
      search: '',
      active: true,
      page: 1,
    },
    companies: {} as Record<string, unknown> | Tenant[],
    company: emptyTenant(),
  }),
  actions: {
    async getCompanies(all = false) {
      const appStore = useAppStore()

      appStore.toggleLoading(true)
      try {
        const response = await api.get(`${all ? '/admin/tenants?all=true' : '/admin/tenants'}`, {
          params: {
            search: this.filters.search,
            active: this.filters.active ? 1 : 0,
            page: this.filters.page,
          },
        })

        this.companies = response.data.tenants
      } catch (e: any) {
        const { init } = useToast()
        init({ message: getErrorMessage(e), color: 'danger' })
      } finally {
        appStore.toggleLoading(false)
      }
    },
    async getCompany(id: number) {
      const appStore = useAppStore()

      appStore.toggleLoading(true)
      try {
        const response = await api.get(`/admin/tenants/${id}`)
        const tenant = response.data.tenant as Tenant

        this.company = {
          ...emptyTenant(),
          ...tenant,
          address: tenant?.address ? { ...emptyAddress(), ...tenant.address } : emptyAddress(),
        }
      } catch (e: any) {
        const { init } = useToast()
        init({ message: getErrorMessage(e), color: 'danger' })
      } finally {
        appStore.toggleLoading(false)
      }
    },
    async createCompany(payload: PersistTenantPayload) {
      const appStore = useAppStore()

      appStore.toggleLoading(true)
      try {
        const response = await api.post('/admin/tenants', payload)
        const { init } = useToast()

        init({ message: getResponseMessage(response.data, 'Empresa adicionada com sucesso!'), color: 'success' })

        return response.data.tenant as Tenant
      } catch (e: any) {
        const { init } = useToast()
        init({ message: getErrorMessage(e), color: 'danger' })

        return null
      } finally {
        appStore.toggleLoading(false)
      }
    },
    async updateCompany(id: number, payload: PersistTenantPayload) {
      const appStore = useAppStore()

      appStore.toggleLoading(true)
      try {
        const response = await api.put(`/admin/tenants/${id}`, payload)
        const updated = response.data.tenant as Tenant
        const { init } = useToast()

        this.company = {
          ...this.company,
          ...updated,
          customers: this.company.customers,
          address: updated?.address ? { ...emptyAddress(), ...updated.address } : this.company.address,
        }

        init({ message: getResponseMessage(response.data, 'Empresa atualizada com sucesso!'), color: 'success' })

        return true
      } catch (e: any) {
        const { init } = useToast()
        init({ message: getErrorMessage(e), color: 'danger' })

        return false
      } finally {
        appStore.toggleLoading(false)
      }
    },
  },
})
